import { useContext, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type FormEvent, type FormHTMLAttributes, type ReactNode, type Ref, type RefObject } from "react";
import { createPortal } from "react-dom";
import { SessionContext } from "../hooks";
import { t } from "../i18n";
import { densityInUse, onDisplay } from "../display";
import { place, type Box, type PlaceOptions } from "../place";
import { invalidText } from "../validity";
import { IconClose } from "./icons";

// The building blocks of Lantern's full pages (settings, admin consoles,
// lists): a page frame, cards, fields, switches, badges, stats and empty
// states, styled in design.css.

export function Page({
  title,
  description,
  actions,
  className = "",
  label,
  narrow,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  label?: string;
  // narrow suits preference pages and forms; wide, tables and consoles.
  narrow?: boolean;
  children: ReactNode;
}) {
  return (
    // The page scrolls, so it takes focus, to scroll from the keyboard
    // when nothing on it can.
    <section className={`page ${narrow ? "narrow" : ""} ${className}`} aria-label={label ?? (typeof title === "string" ? title : undefined)} tabIndex={0}>
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          {description && <p className="page-description">{description}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      <div className="page-body">{children}</div>
    </section>
  );
}

export function Card({
  title,
  description,
  actions,
  className = "",
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-header">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p className="card-description">{description}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

// Field is a label, its control and a hint. What is wrong with what was
// typed shows under it instead of the hint: error, as a form says, or, in
// place of the browser's pop-up, what a control that is required or out of
// range needs, in Lantern's language.
export function Field({ label, hint, error, children, className = "" }: { label: ReactNode; hint?: ReactNode; error?: string; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLLabelElement>(null);
  const id = useId();
  const said = useInvalid(ref, id, error);
  return (
    <label ref={ref} className={`field ${className}`}>
      <span className="field-label">{label}</span>
      {children}
      {said ? (
        <span className="field-error" id={id}>
          {said}
        </span>
      ) : (
        hint && <span className="field-hint">{hint}</span>
      )}
    </label>
  );
}

// useInvalid is what is wrong with the control in ref, for its field to show
// with the id given: error, or what the browser found when the form was
// sent, until it is typed over. It marks the control for screen readers.
export function useInvalid(ref: RefObject<HTMLElement | null>, id: string, error?: string): string {
  const [invalid, setInvalid] = useState("");
  const said = error || invalid;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // invalid does not bubble, so it is caught on the way down.
    const on = (e: Event) => {
      e.preventDefault();
      const control = e.target as HTMLInputElement;
      setInvalid(invalidText(control));
      // The first wrong one, as the browser would have it, takes the focus.
      if (!document.activeElement?.matches(":invalid")) control.focus();
    };
    // Typing over it clears it, heard on the document, after React has taken
    // what was typed: clearing it first would draw a controlled control
    // again with its old value, and lose what was typed.
    const typed = (e: Event) => el.contains(e.target as Node) && (e.target as HTMLInputElement).validity?.valid && setInvalid("");
    const doc = el.ownerDocument;
    el.addEventListener("invalid", on, true);
    doc.addEventListener("input", typed);
    doc.addEventListener("change", typed);
    return () => {
      el.removeEventListener("invalid", on, true);
      doc.removeEventListener("input", typed);
      doc.removeEventListener("change", typed);
    };
  }, [ref]);
  useEffect(() => {
    const control = ref.current?.querySelector<HTMLElement>("input, select, textarea");
    if (!control) return;
    if (said) {
      control.setAttribute("aria-invalid", "true");
      control.setAttribute("aria-describedby", id);
    } else {
      control.removeAttribute("aria-invalid");
      control.removeAttribute("aria-describedby");
    }
  }, [ref, said, id]);
  return said;
}

// Form is a form whose submit button turns and takes no more clicks until
// what submitting does, if it returns a promise, is done.
export function Form({ onSubmit, children, ref, ...rest }: Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit"> & { onSubmit: (e: FormEvent<HTMLFormElement>) => unknown; ref?: Ref<HTMLFormElement> }) {
  const [busy, setBusy] = useState(false);
  return (
    <form
      {...rest}
      ref={ref}
      aria-busy={busy || undefined}
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        const done = onSubmit(e);
        if (done instanceof Promise) {
          setBusy(true);
          void done.catch(() => {}).finally(() => setBusy(false));
        }
      }}
    >
      {children}
    </form>
  );
}

// Switch is a toggle. Its label is bound to it by id, so controls in extra
// (a "try it" button) are not taken as what the label names.
export function Switch({
  checked,
  onChange,
  label,
  description,
  extra,
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  extra?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="switch-row">
      <span className="switch-text">
        <label className="switch-label" htmlFor={id}>
          {label}
        </label>
        {(description || extra) && (
          <span className="field-hint">
            {description} {extra}
          </span>
        )}
      </span>
      <input id={id} type="checkbox" role="switch" className="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </div>
  );
}

// useHashTab is the tab of a page that has them, kept in the address's
// hash so a link can open one (/help#search) and back and
// forward move between them; an unknown hash is the first tab.
export function useHashTab<K extends string>(keys: readonly K[]): [K, (k: K) => void] {
  // The hash is kept as it is, so a tab named before the page has loaded
  // what shows it is still the one opened.
  const [hash, setHash] = useState(() => location.hash.slice(1));
  useEffect(() => {
    const on = () => setHash(location.hash.slice(1));
    addEventListener("hashchange", on);
    addEventListener("popstate", on);
    return () => {
      removeEventListener("hashchange", on);
      removeEventListener("popstate", on);
    };
  }, []);
  const active = keys.includes(hash as K) ? (hash as K) : keys[0];
  return [
    active,
    (k) => {
      if (k !== active) history.pushState(null, "", `${location.pathname}#${k}`);
      setHash(k);
    },
  ];
}

// Tabs split a long page into parts, one shown at a time.
export function Tabs<K extends string>({ tabs, active, onChange, label }: { tabs: readonly (readonly [K, ReactNode])[]; active: K; onChange: (k: K) => void; label: string }) {
  return (
    <div className="page-tabs" role="tablist" aria-label={label}>
      {tabs.map(([k, name]) => (
        <button key={k} role="tab" id={`tab-${k}`} aria-selected={active === k} className={active === k ? "active" : ""} onClick={() => onChange(k)}>
          {name}
        </button>
      ))}
    </div>
  );
}

export type Tone = "ok" | "warn" | "bad" | "neutral" | "accent";

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge-pill ${tone}`}>{children}</span>;
}

export function Stat({ label, value, tone }: { label: ReactNode; value: ReactNode; tone?: Tone }) {
  return (
    <div className={`stat ${tone ?? ""}`}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

export function EmptyState({ icon, title, children }: { icon: string; title: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty-state">
      <span className="empty-icon" aria-hidden>
        {icon}
      </span>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
    </div>
  );
}

// Sheet is a dialog over the page: centered on a wide screen, rising from
// the bottom on a phone. Escape or a click outside closes it; it keeps the
// focus while open and gives it back after. With the "page" class its
// content takes the design system's styles; size makes it narrower or
// wider than the usual.
export function Sheet({ title, onClose, className = "", size, children }: { title: string; onClose: () => void; className?: string; size?: "sm" | "lg"; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useDialog(ref, onClose);
  return createPortal(
    <div className="sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`sheet ${size ?? ""} ${className}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <header>
          <h2>{title}</h2>
          <button className="close" aria-label={t("Close")} onClick={onClose}>
            <IconClose size={18} />
          </button>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// The dialogs open, the last on top; only it answers Escape and keeps the
// focus.
const dialogs: HTMLElement[] = [];
const focusable = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// useDialog makes an element a dialog: the focus goes to its first field
// (or one marked autofocus, or the dialog itself), Tab goes round within
// it, Escape closes it, and on closing the focus goes back to what had it.
export function useDialog(ref: RefObject<HTMLElement | null>, onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  // What had the focus is noted on the first drawing, before a field in the
  // dialog takes it.
  const [opener] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null));
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    dialogs.push(el);
    if (!el.contains(document.activeElement)) {
      // React writes no autofocus attribute; a link marks itself instead.
      const first = el.querySelector<HTMLElement>("[autofocus], [data-autofocus]") ?? el.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea");
      (first ?? el).focus({ preventScroll: true });
    }
    const key = (e: KeyboardEvent) => {
      // A popup of the dialog's own, outside it, has the key first, as does
      // a control that used it. When what had the focus went away, the
      // focus is nowhere, and the key is still the dialog's.
      if (dialogs.at(-1) !== el || !(el.contains(e.target as Node) || e.target === document.body) || e.defaultPrevented) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close.current();
      } else if (e.key === "Tab") {
        const all = [...el.querySelectorAll<HTMLElement>(focusable)].filter((x) => x.offsetParent !== null);
        if (all.length === 0) return;
        const [first, last] = [all[0], all[all.length - 1]];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === el)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      dialogs.splice(dialogs.indexOf(el), 1);
      document.removeEventListener("keydown", key);
      const lost = !document.activeElement || document.activeElement === document.body || el.contains(document.activeElement);
      if (lost && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [ref, opener]);
}

// usePlace places a popup by what opened it (see place.ts), once it is
// drawn and its size known, and again when the window changes size; until
// then it is clear, though it can take the focus.
export function usePlace(ref: RefObject<HTMLElement | null>, anchor: Box, opts: PlaceOptions = {}, win: Window = window): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ top: 0, left: 0, opacity: 0 });
  const { side, align, gap } = opts;
  useLayoutEffect(() => {
    const at = () => {
      const el = ref.current;
      if (!el) return;
      const p = place(anchor, { width: el.offsetWidth, height: el.scrollHeight }, { width: win.innerWidth, height: win.innerHeight }, { side, align, gap });
      setStyle({ top: p.top, left: p.left, maxHeight: p.maxHeight, transformOrigin: p.side === "below" ? "top" : "bottom" });
    };
    at();
    win.addEventListener("resize", at);
    return () => win.removeEventListener("resize", at);
  }, [ref, anchor.top, anchor.bottom, anchor.left, anchor.right, side, align, gap, win]);
  return style;
}

// useTip shows a tip about what its props go on: after a moment's hover,
// or at once on keyboard focus, above it, until the pointer or focus
// leaves or Escape is pressed. It stands in for the browser's title, which
// cannot be styled or read on focus.
export function useTip(content: ReactNode) {
  const id = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const timer = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!anchor) return;
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setAnchor(null);
    };
    addEventListener("keydown", key, true);
    return () => removeEventListener("keydown", key, true);
  }, [anchor]);
  const show = (el: HTMLElement, wait: number) => {
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setAnchor(el), wait);
  };
  const hide = () => {
    clearTimeout(timer.current);
    setAnchor(null);
  };
  return {
    props: {
      onMouseEnter: (e: { currentTarget: HTMLElement }) => show(e.currentTarget, 450),
      onMouseLeave: hide,
      onFocus: (e: { currentTarget: HTMLElement }) => e.currentTarget.matches(":focus-visible") && show(e.currentTarget, 0),
      onBlur: hide,
      onMouseDown: hide,
      "aria-describedby": anchor ? id : undefined,
    },
    tip: anchor && (
      <TipBox id={id} anchor={anchor}>
        {content}
      </TipBox>
    ),
  };
}

function TipBox({ id, anchor, children }: { id: string; anchor: HTMLElement; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box] = useState(() => anchor.getBoundingClientRect());
  const style = usePlace(ref, box, { side: "above", align: "center" });
  return createPortal(
    <div ref={ref} id={id} role="tooltip" className="tip" style={style}>
      {children}
    </div>,
    document.body,
  );
}

// useDismiss closes a popup on a press outside it (and outside keep, as
// the button that opened it) or on Escape, which it keeps from closing a
// dialog or thread under it too.
export function useDismiss(ref: RefObject<HTMLElement | null>, onClose: () => void, { keep, win = window, open = true }: MenuOptions = {}) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => {
      const at = e.target as Element;
      // The button that opened it, marked expanded, closes it itself.
      if (ref.current?.contains(at) || keep?.current?.contains(at) || at.closest?.('[aria-haspopup][aria-expanded="true"]')) return;
      close.current();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      close.current();
    };
    win.addEventListener("mousedown", down, true);
    win.addEventListener("keydown", key, true);
    return () => {
      win.removeEventListener("mousedown", down, true);
      win.removeEventListener("keydown", key, true);
    };
  }, [ref, keep, win, open]);
}

// MenuOptions: keep is what else does not close it when pressed; win the
// window it is in; open, for a menu always drawn, whether it shows.
type MenuOptions = { keep?: RefObject<HTMLElement | null>; win?: Window; open?: boolean };

// useMenu gives a menu its keys: the first item takes the focus, the
// arrows, Home and End move it, and the pointer moves it as it goes, so
// mouse and keys show one highlight. It closes as useDismiss has it, and
// gives the focus back to what opened it.
export function useMenu(ref: RefObject<HTMLElement | null>, onClose: () => void, opts: MenuOptions = {}) {
  useDismiss(ref, onClose, opts);
  const win = opts.win ?? window;
  const open = opts.open ?? true;
  useEffect(() => {
    const el = ref.current;
    if (!el || !open) return;
    const opener = win.document.activeElement as HTMLElement | null;
    const items = () => [...el.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])')].filter((x) => x.offsetParent !== null);
    items()[0]?.focus({ preventScroll: true });
    const key = (e: KeyboardEvent) => {
      const all = items();
      const at = all.indexOf(win.document.activeElement as HTMLElement);
      const to = { ArrowDown: at + 1, ArrowUp: at - 1, Home: 0, End: all.length - 1 }[e.key];
      if (to === undefined || all.length === 0) return;
      e.preventDefault();
      all[(to + all.length) % all.length].focus();
    };
    const move = (e: MouseEvent) => {
      const item = (e.target as HTMLElement).closest<HTMLElement>('[role^="menuitem"]');
      if (item && el.contains(item) && win.document.activeElement !== item) item.focus({ preventScroll: true });
    };
    el.addEventListener("keydown", key);
    el.addEventListener("mousemove", move);
    return () => {
      el.removeEventListener("keydown", key);
      el.removeEventListener("mousemove", move);
      const active = win.document.activeElement;
      if ((!active || active === win.document.body || el.contains(active)) && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [ref, win, open]);
}

// useDensity is how close together messages sit: "" or compact.
export function useDensity() {
  return useSyncExternalStore(onDisplay, densityInUse);
}

// Avatar is a person's profile photo, or their initials on a color drawn
// from their id, each as light as the others, and dark enough for white
// (4.8:1 at least).
export function Avatar({ id, name, size, presence }: { id: string; name: string; size: number; presence?: string }) {
  const photo = usePhoto(id);
  const initials = name
    .split(/\s+/)
    .filter((w) => /^[\p{L}\p{N}]/u.test(w))
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: `oklch(0.54 0.09 ${h})` }} aria-hidden>
      {photo ? <img src={photo} alt="" /> : initials || "?"}
      {presence && <span className={`presence ${presence}`} />}
    </span>
  );
}

const noStore = () => () => {};

// usePhoto is someone's profile photo URL once loaded, kept current as they
// change it; outside a session, none.
function usePhoto(id: string): string {
  const session = useContext(SessionContext);
  useSyncExternalStore(session?.store.subscribe ?? noStore, () => session?.store.getSnapshot().profileBumps ?? 0);
  const pending = session?.avatarURL(id);
  const [url, setURL] = useState("");
  useEffect(() => {
    let live = true;
    if (!pending) return setURL("");
    pending.then(
      (u) => live && setURL(u),
      () => live && setURL(""),
    );
    return () => {
      live = false;
    };
  }, [pending]);
  return pending ? url : "";
}
